/**
 * API calls for listing, accepting, and declining student invite requests.
 * Connects to the Arc Mosaico backend endpoints.
 */

import { api } from 'helpers'
import { ARC_MOSAICO_ENDPOINTS } from 'api/arcMosaico'
import type {
  StudentRequestsResponse,
  AcceptRequestPayload,
  GroupOption,
  GradeOption,
} from 'api/arcMosaico/types'
import { UnauthorizedError } from './unauthorizedError'

export { isUnauthorizedError, UnauthorizedError } from './unauthorizedError'

/** Groups endpoint returns rich objects; we extract what we need */
interface GroupsApiResponse {
  groups: Array<{
    id: string
    name: string
    course?: { id: string; name: string }
  }>
}

/** Result from getGroupOptions — includes courseId for create-group calls */
export interface GroupOptionsResult {
  groups: GroupOption[]
  courseId: string | null
}

/**
 * Guard for 401 responses. The axios interceptor does NOT reject on 401
 * (it returns the response directly), so we must check status manually.
 */
function assertAuthenticated(response: { status: number }): void {
  if (response.status === 401) {
    window.location.href = '/error_permission'
    throw new Error('Unauthorized')
  }
}

/**
 * @param silentUnauthorized Background callers (badge polling) pass true: a
 * 401 throws a typed UnauthorizedError instead of redirecting the user to
 * /error_permission — an idle session expiry must not hijack the page.
 * Interactive callers keep the default redirect behaviour.
 */
export async function getStudentRequests(
  page = 1,
  limit = 20,
  { silentUnauthorized = false }: { silentUnauthorized?: boolean } = {},
): Promise<StudentRequestsResponse> {
  const response = await api.get(
    ARC_MOSAICO_ENDPOINTS.studentRequests.list,
    { params: { page, limit } }
  )
  if (silentUnauthorized && response.status === 401) throw new UnauthorizedError()
  assertAuthenticated(response)
  return response.data as StudentRequestsResponse
}

export async function acceptStudentRequest(id: string, payload: AcceptRequestPayload): Promise<void> {
  const response = await api.post(ARC_MOSAICO_ENDPOINTS.studentRequests.accept(id), payload)
  assertAuthenticated(response)
}

export async function declineStudentRequest(id: string): Promise<void> {
  const response = await api.post(ARC_MOSAICO_ENDPOINTS.studentRequests.decline(id))
  assertAuthenticated(response)
}

export async function getGroupOptions(): Promise<GroupOptionsResult> {
  const response = await api.get(ARC_MOSAICO_ENDPOINTS.groups.list, {
    params: { limit: 100 },
  })
  assertAuthenticated(response)

  const data = response.data as GroupsApiResponse
  const groups: GroupOption[] = (data.groups || []).map(g => ({ id: g.id, name: g.name }))
  const courseId = data.groups?.[0]?.course?.id ?? null

  return { groups, courseId }
}

export async function getGradeOptions(): Promise<GradeOption[]> {
  const response = await api.get(ARC_MOSAICO_ENDPOINTS.grades.list)
  assertAuthenticated(response)
  return response.data as GradeOption[]
}

export async function createGroupQuick(name: string): Promise<GroupOption> {
  const response = await api.post(ARC_MOSAICO_ENDPOINTS.groups.create, {
    name,
  })
  assertAuthenticated(response)
  return response.data as GroupOption
}
